<template>
  <ais-pagination>
    <template
      #default="{
        currentRefinement,
        nbPages,
        isFirstPage,
        isLastPage,
        refine,
        createURL
      }"
    >
      <ul
        class="algolia-pagination"
        :class="{ [$class('hidden')]: noPages }"
      >
        <li
          class="algolia-pagination__prev"
          :class="{ [$class('disabled')]: isFirstPage}"
        >
          <a
            :href="createURL(currentRefinement - 1)"
            @click.prevent="pageChangeHandler(() => refine(currentRefinement - 1))"
          >
            <icon-chevron-left />
          </a>
        </li>
        <li
          v-for="page in visiblePages(currentRefinement, nbPages)"
          :key="page.key"
          class="algolia-pagination__item"
        >
          <a
            v-if="page.number !== null"
            :class="{
              'text-body-2xs': page.number !== currentRefinement,
              'text-body-2xs-bold': page.number === currentRefinement,
            }"
            :href="createURL(page.number)"
            @click.prevent="pageChangeHandler(() => refine(page.number))"
            v-text="page.number + 1"
          />
          <span
            v-else
            :class="{
              'text-body-2xs': page.number !== currentRefinement,
              'text-body-2xs-bold': page.number === currentRefinement,
            }"
            v-text="page.label"
          />
        </li>
        <li
          class="algolia-pagination__next"
          :class="{ [$class('disabled')]: isLastPage}"
        >
          <a
            :href="createURL(currentRefinement + 1)"
            @click.prevent="pageChangeHandler(() => refine(currentRefinement + 1))"
          >
            <icon-chevron-right />
          </a>
        </li>
      </ul>
    </template>
  </ais-pagination>
</template>

<script>
import { AisPagination } from 'vue-instantsearch/vue3/es'
import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

/**
 * Vue: Algolia pagination (algolia-pagination)
 * -----------------------------------------------------------------------------
 * Slider component for displaying range based algolia filtering properties.
 *
 */

export default {
  name: 'AlgoliaPagination',

  components: {
    AisPagination,
    IconChevronLeft,
    IconChevronRight,
  },

  emits: ['page-change'],

  data() {
    return {
      noPages: true,
    }
  },

  methods: {

    pageChangeHandler(callback) {
      callback()
      this.$emit('page-change')
    },

    /**
     * Returns pages to render in pagination component.
     * @param currentPage - number: the number of the selected page.
     * @param totalPages - number: the total number of pages.
     */
    visiblePages(currentPage, totalPages) {
      const pages = []

      if(totalPages < 6) {
        for(let index = 0; index < totalPages; index++) {
          pages.push({ key: index, number: index, label: index })
        }
      } else if (currentPage === 0) {
        pages.push(
          { key: 0, number: 0, label: 0 },
          { key: 1, number: 1, label: 1 },
          { key: 'dots', number: null, label: '...' },
          { key: totalPages - 2, number: totalPages - 2, label: totalPages - 2 },
          { key: totalPages - 1, number: totalPages - 1, label: totalPages - 1 },
        )
      } else if (currentPage < 3) {
        pages.push(
          { key: 0, number: 0, label: 0 },
          { key: 1, number: 1, label: 1 },
          { key: 2, number: 2, label: 2 },
          { key: 3, number: 3, label: 3 },
          { key: 'dots1', number: null, label: '...' },
          { key: totalPages - 1, number: totalPages - 1, label: totalPages - 1, active: currentPage === totalPages - 1 },
        )
      } else if (currentPage >= 3 && currentPage < totalPages - 3) {
        pages.push(
          { key: 0, number: 0, label: 0 },
          { key: 'dots2', number: null, label: '...' },
          { key: currentPage - 1, number: currentPage - 1, label: currentPage - 1 },
          { key: currentPage, number: currentPage, label: currentPage },
          { key: currentPage + 1, number: currentPage + 1, label: currentPage + 1 },
          { key: 'dots3', number: null, label: '...' },
          { key: totalPages - 1, number: totalPages - 1, label: totalPages - 1 },
        )
      } else if (currentPage >= 3 && currentPage < totalPages - 2) {
        pages.push(
          { key: 0, number: 0, label: 0 },
          { key: 'dots4', number: null, label: '...' },
          { key: currentPage - 1, number: currentPage - 1, label: currentPage - 1 },
          { key: currentPage, number: currentPage, label: currentPage },
          { key: currentPage + 1, number: currentPage + 1, label: currentPage + 1 },
          { key: currentPage + 2, number: currentPage + 2, label: currentPage + 2 },
        )
      } else if (currentPage >= 3 && currentPage < totalPages - 1) {
        pages.push(
          { key: 0, number: 0, label: 0 },
          { key: 'dots5', number: null, label: '...' },
          { key: currentPage - 1, number: currentPage - 1, label: currentPage - 1 },
          { key: currentPage, number: currentPage, label: currentPage },
          { key: currentPage + 1, number: currentPage + 1, label: currentPage + 1 },
        )
      } else if (currentPage >= 3 && currentPage < totalPages) {
        pages.push(
          { key: 0, number: 0, label: 0 },
          { key: 'dots6', number: null, label: '...' },
          { key: currentPage - 2, number: currentPage - 2, label: currentPage - 2 },
          { key: currentPage - 1, number: currentPage - 1, label: currentPage - 1 },
          { key: currentPage, number: currentPage, label: currentPage },
        )
      }

      this.noPages = pages.length === 0

      return pages
    },
  },
}
</script>
